import { useMemo } from "react";
import useActivityStore, { ActivityItem } from "@/store/zustand/activityStore";
import { Message } from "@/store/zustand/supabaseStore";
import { ManualAssistantMessage } from "@/store/zustand/aiContextStore";
import { MessageRestoreState } from "./useMessageRestoreState";
import {
  extractActivityIdsFromMessage,
  findLatestActivity,
  checkIfCurrentHead,
  checkIfOverwritten,
} from "../utils/activityUtils";

export type MessageBlock = {
  type: "valid" | "discarded";
  messages: (Message | ManualAssistantMessage)[];
};

interface MessageMetadata {
  activityIds: string[];
  isDiscarded: boolean;
  restoreState: MessageRestoreState;
}

/**
 * Computes restore state for a message given its activity IDs
 */
const computeRestoreState = (
  activityIds: string[],
  activityStreamList: ActivityItem[],
  activityStreamLookup: Record<string, ActivityItem>,
): MessageRestoreState => {
  // Early return if no activities found
  if (activityIds.length === 0) {
    return {
      latestActivity: null,
      isCurrentHead: false,
      isOverwritten: false,
    };
  }

  const { activity, hasOverwrittenActivities } = findLatestActivity(
    activityIds,
    activityStreamLookup,
  );

  // Early return if no valid activity found
  if (!activity) {
    return {
      latestActivity: null,
      isCurrentHead: false,
      isOverwritten: hasOverwrittenActivities,
    };
  }

  const isCurrentHead = checkIfCurrentHead(activity, activityStreamList);
  const isOverwritten = checkIfOverwritten(
    activity,
    hasOverwrittenActivities,
    activityStreamLookup,
  );

  return {
    latestActivity: activity,
    isCurrentHead,
    isOverwritten,
  };
};

/**
 * Checks if a message should be marked as discarded
 */
const checkIfDiscarded = (
  activityIds: string[],
  activityStreamLookup: Record<string, ActivityItem>,
): boolean => {
  if (activityIds.length === 0) return false;

  // Check if this message has any overwritten activities (not in stream at all)
  const hasOverwrittenActivity = activityIds.some(
    (id) => !activityStreamLookup[id],
  );

  // Check if this message has any undone activities
  const hasUndoneActivity = activityIds.some((id) => {
    const activity = activityStreamLookup[id];
    return activity && activity.change_undone;
  });

  return hasOverwrittenActivity || hasUndoneActivity;
};

/**
 * Custom hook that computes message blocks and restore states in a single pass
 *
 * This hook optimizes performance by:
 * 1. Parsing each message only ONCE (not twice like before)
 * 2. Computing all metadata (activityIds, discarded state, restore state) in a single pass
 * 3. Caching results in a Map for O(1) lookup by MessageRestoreButton
 *
 * @returns Object containing:
 *   - messageBlocks: Array of valid/discarded message groups for rendering
 *   - getRestoreState: Function to get cached restore state for a message
 */
export const useMessageBlocks = (
  messages: (Message | ManualAssistantMessage)[],
  isChatLoading: boolean = false,
) => {
  const activityStreamList = useActivityStore(
    (state) => state.activityStreamList,
  );
  const activityStreamLookup = useActivityStore(
    (state) => state.activityStreamLookup,
  );

  return useMemo(() => {
    const filteredMessages = messages.filter(
      (msg) => (msg as Message).role !== "tool-result",
    );

    const lastMessageId = filteredMessages[filteredMessages.length - 1]?.id;

    // Step 1: Compute metadata for all messages in a single pass
    const messageMetadataMap = new Map<string, MessageMetadata>();

    filteredMessages.forEach((message) => {
      const role = (message as Message).role;

      // Only process assistant messages for metadata
      if (role !== "assistant") return;

      // Parse and extract activity IDs ONCE
      const activityIds = extractActivityIdsFromMessage(message as Message);

      // Compute discarded state
      // Skip marking the last message as discarded while chat is loading to prevent flicker
      const isLastMessageWhileLoading =
        isChatLoading && message.id === lastMessageId;
      const isDiscarded =
        !isLastMessageWhileLoading &&
        checkIfDiscarded(activityIds, activityStreamLookup);

      // Compute restore state
      const restoreState = computeRestoreState(
        activityIds,
        activityStreamList,
        activityStreamLookup,
      );

      // Cache metadata for later retrieval
      messageMetadataMap.set(message.id, {
        activityIds,
        isDiscarded,
        restoreState,
      });
    });

    // Helper function to check if a message is discarded (uses cached metadata)
    const isMessageDiscarded = (
      message: Message | ManualAssistantMessage,
    ): boolean => {
      const metadata = messageMetadataMap.get(message.id);
      return metadata?.isDiscarded ?? false;
    };

    // Step 2: Build message blocks by grouping consecutive discarded messages
    const blocks: MessageBlock[] = [];
    let currentBlock: MessageBlock | null = null;

    for (let i = 0; i < filteredMessages.length; i++) {
      const message = filteredMessages[i];
      const isAssistant = (message as Message).role === "assistant";
      const isDiscarded = isAssistant && isMessageDiscarded(message);

      // Determine if this message should start a new block
      const shouldBeDiscardedBlock =
        isDiscarded ||
        (i > 0 && // Include user message before discarded assistant
          (filteredMessages[i] as Message).role === "user" &&
          i + 1 < filteredMessages.length &&
          (filteredMessages[i + 1] as Message).role === "assistant" &&
          isMessageDiscarded(filteredMessages[i + 1]));

      const blockType = shouldBeDiscardedBlock ? "discarded" : "valid";

      // Start new block or add to current block
      if (!currentBlock || currentBlock.type !== blockType) {
        if (currentBlock) {
          blocks.push(currentBlock);
        }
        currentBlock = { type: blockType, messages: [message] };
      } else {
        currentBlock.messages.push(message);
      }
    }

    if (currentBlock) {
      blocks.push(currentBlock);
    }

    // Step 3: Return blocks and a getter for cached restore states
    return {
      messageBlocks: blocks,
      getRestoreState: (messageId: string): MessageRestoreState => {
        const metadata = messageMetadataMap.get(messageId);
        return (
          metadata?.restoreState || {
            latestActivity: null,
            isCurrentHead: false,
            isOverwritten: false,
          }
        );
      },
    };
  }, [messages, activityStreamLookup, activityStreamList, isChatLoading]);
};
